import React, { useContext, useEffect, useRef, useState } from "react"
import { View, StyleSheet, Image, Text } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { connect } from "react-redux"
import { useForm } from "react-hook-form"
import * as amplitude from "@amplitude/analytics-react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import TextM from "@common/components/text/TextM"
import TextInputM from "@common/components/input/TextInputM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { emailRegExp } from "@data/utility/validation"
import { theme } from "@common/theme/theme"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import ClassChipGridM from "@common/components/funnel/ClassChipGridM"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { useRoute } from "@react-navigation/native"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import logo from "@assets/logo-blue.png"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import FunnelStepLayout from "@common/components/funnel/FunnelStepLayout"
import { FUNNEL_SCENES } from "@common/components/funnel/funnelScenes"
import SummerHelpBanner from "../summer/components/SummerHelpBanner"
import {
    deriveClassRentree,
    normaliseClass,
} from "../summer/constants/summerContent"
import {
    STAGE_FUNNEL_INITIAL,
    useStageFunnel,
} from "./context/StageFunnelContext"
import {
    readLeadIntentParam,
    readSourceParam,
    readTemplateParam,
    useStageFunnelGuard,
} from "./hooks/useStageFunnelGuard"
import { useStageTrack } from "./hooks/useStageTrack"
import { useStageFunnelConfig } from "./hooks/useStageFunnelConfig"
import { stageFunnelService } from "./services/stageFunnelService"
import { getTemplateClasses, getTemplateName } from "./constants/stageContent"

// Classes proposées (alignées sur User::$studentClasses backend). La liste
// effective est restreinte au scope niveaux du template (config API) dès que la
// config est chargée — M2M vide côté BO = toutes les classes.
const STUDENT_CLASSES = [
    "CE2",
    "CM1",
    "CM2",
    "6ème",
    "5ème",
    "4ème",
    "3ème",
    "2nde",
    "1ère",
    "Tle",
    "Autre",
]

const FUNNEL_TOTAL_STEPS = 6
// Largeur de l'écran d'indisponibilité, qui ne passe pas par le gabarit.
const CONTENT_MAX = 460

/**
 * Étape 1 du funnel Stage — clone de l'écran classe du funnel été : UN SEUL
 * écran qui regroupe la classe de cette année ET la classe à la rentrée
 * (pré-remplie par dérivation, corrigeable), rendues en grilles de pastilles
 * compactes. Deux différences stage :
 *  - le scope de classes vient du template (catalogue API) dès qu'il est chargé ;
 *  - un lead_intent inconnu / un stage hors vente rend l'écran « stage
 *    indisponible » (pas de vente) au lieu du parcours.
 */
const StageClassScreen = (props) => {
    const {
        navigation,
        reduxFunnelStateId,
        reduxInitUser,
        reduxTemplateSlug,
        reduxProfileKnown,
        reduxStudentClass,
        initError,
        startError,
        startInFlight,
    } = props
    const route = useRoute()
    const dimensions = useContext(DimensionsContext)
    const windowWidth = dimensions?.width || 400
    const { state, update, reset } = useStageFunnel()
    // Refresh-safe / URL-partageable (voir useStageFunnelGuard). L'écran de
    // départ n'a pas de redirection « retour au début » (isStart) mais
    // ré-hydrate via stage/resume si un `user` arrive sur un contexte froid.
    const { nextParams } = useStageFunnelGuard({ isStart: true })
    const track = useStageTrack()

    // ===== Reset d'identité (URL ≠ contexte repris) =====
    // Quand l'URL d'entrée porte une AUTRE identité que le contexte
    // sessionStorage (nouveau submissionGUID, ou email explicite différent),
    // le contexte est reset — mais le funnelStateId / initUser REDUX de
    // l'ancienne identité survivent au reset (le reducer stageFunnel n'est
    // vidé qu'au logout). Ces refs mémorisent les valeurs périmées pour que
    // les effets de sync ne ré-hydratent JAMAIS le contexte remis à zéro avec
    // l'ancien state serveur ; un nouveau /start (ou /init) les remplace.
    const staleReduxStateIdRef = useRef(null)
    const staleInitUserRef = useRef(null)
    const effectiveReduxStateId =
        reduxFunnelStateId === staleReduxStateIdRef.current
            ? null
            : reduxFunnelStateId
    const effectiveInitUser =
        reduxInitUser === staleInitUserRef.current ? null : reduxInitUser

    const [studentClass, setStudentClass] = useState(state.studentClass || null)
    // « Continuer » a lancé la création du parcours et attend sa réponse.
    const [awaitingStart, setAwaitingStart] = useState(false)

    // ===== Entrée URL nue : identité du lead collectée INLINE =====
    // Sans email (ni query param, ni HubSpot /init, ni contexte), aucun état
    // serveur ne peut être créé (/start exige prénom + email + classe) → le
    // parcours mourrait plus loin (matières, programme, paiement). On collecte
    // donc ici les champs manquants — jamais de funnel sans state.
    const knownEmail =
        state.parentEmail ||
        route.params?.email ||
        effectiveInitUser?.email ||
        null
    const knownFirstname =
        state.studentFirstname ||
        route.params?.firstname ||
        effectiveInitUser?.firstname ||
        null
    const needsLeadCapture = !knownEmail || !knownFirstname
    const {
        control: leadControl,
        getValues: getLeadValues,
        setError: setLeadError,
        clearErrors: clearLeadErrors,
        formState: { errors: leadErrors },
    } = useForm({ defaultValues: { firstname: "", email: "" } })

    // Valide et récupère les champs d'identité saisis inline — `null` = erreurs
    // affichées sous les champs (validation basique : requis + format email).
    const collectLeadCapture = () => {
        if (!needsLeadCapture) return {}
        clearLeadErrors()
        const values = getLeadValues()
        const collected = {}
        let invalid = false
        if (!knownFirstname) {
            const firstname = (values.firstname || "").trim()
            if (!firstname) {
                setLeadError("firstname", {
                    type: "required",
                    message: "Indiquez le prénom de votre enfant.",
                })
                invalid = true
            } else {
                collected.firstname = firstname
            }
        }
        if (!knownEmail) {
            const email = (values.email || "").trim()
            if (!emailRegExp.test(email)) {
                setLeadError("email", {
                    type: "pattern",
                    message: "Email invalide",
                })
                invalid = true
            } else {
                collected.email = email
            }
        }
        return invalid ? null : collected
    }

    // Config catalogue (scope de classes + nom du stage) dès qu'un état existe.
    const stateId = state.funnelStateId || effectiveReduxStateId
    const { config } = useStageFunnelConfig(stateId)
    const templateClasses = getTemplateClasses(config)
    const templateName = getTemplateName(config) || state.templateName

    // Crée l'état serveur du funnel (entrée directe, sans GUID HubSpot) —
    // UNIQUEMENT quand prénom, classe et email sont réellement connus : le
    // backend exige un prénom non vide (min:1) et une classe valide, et on
    // n'invente JAMAIS une classe par défaut (elle piloterait les matières et
    // le programme d'un autre élève que le vrai). Si une donnée manque, l'état
    // sera créé plus tard (persistAndContinue), une fois la classe choisie.
    const maybeStartFunnel = ({
        firstname,
        klass,
        email,
        rentree,
        templateSlug,
        leadIntent,
    }) => {
        if (!firstname || !klass || !email) return
        props.apiRequest(
            stageFunnelService.startStageFunnel,
            {},
            {
                firstname,
                studentClass: klass,
                studentClassRentree:
                    rentree || deriveClassRentree(klass) || undefined,
                email,
                templateSlug: templateSlug || state.templateSlug,
                leadIntent: leadIntent || state.leadIntent,
                // Lue aussi sur l'URL : au montage, la garde vient à peine de
                // la poser dans le contexte.
                source: readSourceParam(route) || state.entrySource,
            }
        )
    }

    // Hydratation depuis query params / HubSpot (1x au mount).
    useEffect(() => {
        const params = route.params || {}
        const submissionGUID =
            params.submissionGUID || params.submission_guid || null
        // lead_intent transmis par le dispatch FastTrackWelcomeScreen (préfixé
        // stage_ — porte le stage/saison visé) ou par l'URL d'entrée directe.
        const leadIntent = readLeadIntentParam(route)
        const templateSlug =
            params.template ||
            params.template_slug ||
            params.templateSlug ||
            null

        // ===== Mismatch d'identité : reset complet du contexte =====
        // Deux cas où le contexte repris (sessionStorage) appartient à une
        // AUTRE identité que celle de l'URL d'entrée :
        //  - nouveau submissionGUID HubSpot (nouvelle soumission) ;
        //  - email EXPLICITE de l'URL ≠ parentEmail du contexte (2ᵉ enfant,
        //    email corrigé, poste partagé) — sans reset, l'UI affiche le
        //    prénom de l'URL mais answers/programme/paiement partent
        //    silencieusement sur l'ancien state/user backend.
        // Une URL SANS email ne déclenche jamais ce reset.
        const normalizeEmail = (value) =>
            String(value || "")
                .trim()
                .toLowerCase()
        const urlEmail =
            typeof params.email === "string" && params.email.trim()
                ? params.email.trim()
                : null
        const guidMismatch =
            submissionGUID &&
            state.submissionGUID &&
            state.submissionGUID !== submissionGUID
        const emailMismatch =
            !!urlEmail &&
            !!state.parentEmail &&
            normalizeEmail(urlEmail) !== normalizeEmail(state.parentEmail)
        const identityReset = guidMismatch || emailMismatch
        if (identityReset) {
            reset()
            // Le state id / initUser redux de l'ancienne identité ne doivent
            // JAMAIS ré-hydrater le contexte remis à zéro (cf. refs plus haut).
            staleReduxStateIdRef.current = reduxFunnelStateId
            staleInitUserRef.current = reduxInitUser
        }
        // Le contexte à considérer dans CE run : vide après un reset (le
        // setState du reset est asynchrone — `state` porte encore l'ancienne
        // identité dans cette closure).
        const ctx = identityReset ? STAGE_FUNNEL_INITIAL : state

        const routingPatch = {}
        if (leadIntent && leadIntent !== ctx.leadIntent) {
            routingPatch.leadIntent = leadIntent
        }
        if (templateSlug && templateSlug !== ctx.templateSlug) {
            routingPatch.templateSlug = templateSlug
        }

        if (submissionGUID) {
            update({ submissionGUID, ...routingPatch })
            props.apiRequest(
                stageFunnelService.initStageFromHubSpot,
                {},
                {
                    submission_guid: submissionGUID,
                    // Redondant avec la résolution backend, mais évite un aller-retour
                    // HubSpot quand le dispatch a déjà vu le lead_intent.
                    leadIntent: leadIntent || ctx.leadIntent,
                    templateSlug: templateSlug || ctx.templateSlug,
                }
            )
        } else {
            const klass = normaliseClass(params.class) || ctx.studentClass
            const patch = { ...routingPatch }
            if (params.firstname && !ctx.studentFirstname)
                patch.studentFirstname = params.firstname
            if (klass && !ctx.studentClass) patch.studentClass = klass
            if (params.email && !ctx.parentEmail)
                patch.parentEmail = params.email
            // Hydrate local state too so the chip shows selected and
            // "Continuer" works without a manual click.
            if (identityReset) {
                // Nouvelle identité : les chips de l'ancien enfant sautent —
                // on repart des params (ou de rien).
                setStudentClass(klass || null)
                const rentree = deriveClassRentree(klass)
                if (rentree) patch.studentClassRentree = rentree
            } else {
                if (klass && !studentClass) setStudentClass(klass)
                if (klass && !ctx.studentClassRentree) {
                    const rentree = deriveClassRentree(klass)
                    if (rentree) patch.studentClassRentree = rentree
                }
            }
            if (Object.keys(patch).length) update(patch)

            // Après un reset d'identité, l'ancien funnelStateId (contexte OU
            // redux) ne compte pas : un NOUVEAU /start est requis pour créer
            // l'état serveur de la nouvelle identité.
            const effectiveEmail = params.email || ctx.parentEmail
            const noUsableStateId =
                identityReset || (!ctx.funnelStateId && !reduxFunnelStateId)
            if (noUsableStateId && effectiveEmail) {
                maybeStartFunnel({
                    firstname: params.firstname || ctx.studentFirstname,
                    klass,
                    email: effectiveEmail,
                    templateSlug: templateSlug || ctx.templateSlug,
                    leadIntent: leadIntent || ctx.leadIntent,
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Aligne le funnel_state_id (success start/init) dans le context — en
    // ignorant l'id d'une identité résetée (cf. staleReduxStateIdRef) : seul
    // un NOUVEAU /start ou /init peut ré-attacher un état serveur. La ref est
    // lue DANS l'effet (pas via la valeur dérivée du render) : au commit de
    // mount, elle est posée par l'effet d'hydratation qui court juste avant.
    useEffect(() => {
        if (
            reduxFunnelStateId &&
            reduxFunnelStateId !== staleReduxStateIdRef.current &&
            reduxFunnelStateId !== state.funnelStateId
        ) {
            update({ funnelStateId: reduxFunnelStateId })
        }
    }, [reduxFunnelStateId, state.funnelStateId, update])

    // Aligne le template_slug résolu par /init ou /start (lead_intent HubSpot)
    // dans le context AVANT le configurateur — le premier fetch matières part
    // ainsi déjà scopé sur le template (classe ∩ scope M2M), jamais large.
    useEffect(() => {
        if (reduxTemplateSlug && reduxTemplateSlug !== state.templateSlug) {
            update({ templateSlug: reduxTemplateSlug })
        }
    }, [reduxTemplateSlug, state.templateSlug, update])

    // Pousse le user pré-rempli (/init) dans le Context (state.* gagne si déjà
    // saisi) — l'initUser d'une identité résetée est ignoré (stale ref, lue
    // DANS l'effet pour couvrir le commit de mount).
    useEffect(() => {
        if (!reduxInitUser || reduxInitUser === staleInitUserRef.current) return
        const patch = {}
        if (reduxInitUser.firstname && !state.studentFirstname)
            patch.studentFirstname = reduxInitUser.firstname
        if (reduxInitUser.email && !state.parentEmail)
            patch.parentEmail = reduxInitUser.email
        if (reduxInitUser.student_class && !studentClass) {
            patch.studentClass = reduxInitUser.student_class
            setStudentClass(reduxInitUser.student_class)
        }
        if (reduxInitUser.student_class_rentree && !state.studentClassRentree) {
            patch.studentClassRentree = reduxInitUser.student_class_rentree
        }
        if (Object.keys(patch).length) update(patch)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduxInitUser])

    // L'arrivée, dénominateur du drop : émise sans attendre l'identifiant du
    // parcours, une famille qui repart avant est le décrochage à mesurer.
    useEffect(() => {
        // Une entrée par lien passe d'abord par la reprise, qui remonte cet
        // écran : l'arrivée se compte une fois, à ce second montage.
        // Le jeton se lit sur la route, pas dans l'URL : la garde y réécrit
        // celui d'une famille déjà connue de l'onglet, qui n'arrive pas par lien.
        const linkToken = route.params?.user
        if (linkToken && state.resumedToken !== linkToken) return
        track("stage_funnel_started", {
            lead_intent: readLeadIntentParam(route) || state.leadIntent || null,
            template_slug:
                readTemplateParam(route) || state.templateSlug || null,
            source: readSourceParam(route) || state.entrySource || null,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // L'arrivée est partie avant que l'identifiant n'existe : en propriété
    // utilisateur, elle reste joignable au reste du parcours.
    useEffect(() => {
        if (!state.funnelStateId) return
        const identifyObj = new amplitude.Identify()
        identifyObj.set("funnel_state_id", state.funnelStateId)
        amplitude.identify(identifyObj)
    }, [state.funnelStateId])

    const onSelectClass = (c) => setStudentClass(c)

    const persistAndContinue = (collected = {}) => {
        // La classe de rentrée n'est plus demandée : elle se déduit de la
        // classe actuelle, pour les lecteurs en aval qui l'attendent.
        const rentree = deriveClassRentree(studentClass)
        const patch = { studentClass, studentClassRentree: rentree }
        // Identité saisie inline (entrée URL nue) : elle entre dans le contexte
        // AVANT la création de l'état serveur.
        if (collected.firstname) patch.studentFirstname = collected.firstname
        if (collected.email) patch.parentEmail = collected.email
        update(patch)
        // Jamais l'id d'une identité résetée (stale ref) : sans nouvel état
        // serveur, on repasse par /start ci-dessous.
        const currentStateId = state.funnelStateId || effectiveReduxStateId
        if (currentStateId) {
            props.apiRequest(
                stageFunnelService.updateStageAnswers,
                { stateId: currentStateId },
                { studentClass, studentClassRentree: rentree }
            )
        } else {
            // Pas encore de parcours : la classe est maintenant choisie et
            // l'identité connue (voire saisie ici), on le crée — sans relancer
            // une création déjà partie au montage. L'échec d'un essai
            // précédent est effacé : seul celui de CET essai finit l'attente.
            if (!startInFlight) {
                props.apiIdle(stageFunnelService.startStageFunnel)
                maybeStartFunnel({
                    firstname: knownFirstname || collected.firstname,
                    klass: studentClass,
                    email: knownEmail || collected.email,
                    rentree,
                })
            }
            // On n'avance qu'une fois le parcours créé : l'étape suivante
            // sans identifiant passerait pour un parcours perdu. Un échec
            // reste ici, sous le bandeau « Réessayer ».
            setAwaitingStart(true)
            return
        }
        goToDiagnostic(currentStateId)
    }

    const goToDiagnostic = (stateId) => {
        track("stage_funnel_class_completed", { funnel_state_id: stateId })
        navigation.navigate("stage/diagnostic", nextParams)
    }

    // ===== Abonné reconnu (/start, /init) =====
    // Le serveur connaît sa classe (celle du profil, qu'il renvoie, jamais
    // celle du lien) et ses spécificités : il commence au choix des semaines,
    // sans retour vers les deux étapes sautées. Sa réponse remplace dans les
    // deux sens ce que la reprise avait dit (abonnement fini entre-temps).
    // Jamais pour l'état d'une identité résetée : la ref est lue DANS l'effet,
    // posée au commit de montage par l'effet d'hydratation.
    useEffect(() => {
        if (
            !reduxFunnelStateId ||
            reduxFunnelStateId === staleReduxStateIdRef.current
        )
            return
        if (!reduxProfileKnown) {
            if (state.profileKnown) update({ profileKnown: false })
            return
        }
        const klass = reduxStudentClass || state.studentClass
        update({
            profileKnown: true,
            funnelStateId: reduxFunnelStateId,
            studentClass: klass,
            studentClassRentree: deriveClassRentree(klass),
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduxProfileKnown, reduxFunnelStateId])

    useEffect(() => {
        if (!state.profileKnown || !state.funnelStateId) return
        setAwaitingStart(false)
        navigation.reset({
            index: 0,
            routes: [{ name: "stage/configurator", params: nextParams }],
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.profileKnown, state.funnelStateId])

    useEffect(() => {
        // Un abonné reconnu part au choix des semaines (effet ci-dessus).
        if (!awaitingStart || reduxProfileKnown) return
        const createdStateId = state.funnelStateId || effectiveReduxStateId
        if (createdStateId) {
            setAwaitingStart(false)
            goToDiagnostic(createdStateId)
        } else if (startError) {
            setAwaitingStart(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awaitingStart, state.funnelStateId, effectiveReduxStateId, startError])

    // CTA : valide (et récupère) l'identité inline avant de continuer.
    const onContinuePress = () => {
        const collected = collectLeadCapture()
        if (collected === null) return
        persistAndContinue(collected)
    }

    const firstname =
        state.studentFirstname || route.params?.firstname || "votre enfant"

    // Classes limitées au scope niveaux du template (config API). M2M vide (ou
    // config pas encore chargée) → toutes les classes.
    const scopedClasses = templateClasses
        ? STUDENT_CLASSES.filter((c) => templateClasses.includes(c))
        : STUDENT_CLASSES
    const effectiveClasses = scopedClasses.length
        ? scopedClasses
        : STUDENT_CLASSES
    const classOptions = effectiveClasses.map((c) => ({ id: c, label: c }))

    // ===== Stage indisponible : lead_intent inconnu / saison hors vente =====
    // Le backend refuse l'init (404/422 ou code explicite) → pas de vente, on
    // l'affiche clairement au parent (pas de parcours mort-né). Pour /start, un
    // 422 peut aussi être une simple erreur de validation (email invalide…) →
    // seuls le 404 et le code explicite déclenchent l'écran indisponible.
    const isUnavailable = (e, { strict } = {}) =>
        e &&
        (e.error === "stage_unavailable" ||
            e.status === 404 ||
            (!strict && e.status === 422))
    const unavailableError = isUnavailable(initError)
        ? initError
        : isUnavailable(startError, { strict: true })
          ? startError
          : null
    if (unavailableError) {
        return (
            <View style={styles.root}>
                <View
                    style={[
                        styles.frame,
                        styles.unavailableFrame,
                        { width: Math.min(windowWidth, CONTENT_MAX) },
                    ]}
                >
                    <Image
                        source={logo}
                        style={styles.unavailableLogo}
                        resizeMode="contain"
                    />
                    <View style={styles.unavailableIcon}>
                        <FontAwesome5
                            name="calendar-times"
                            size={28}
                            color={theme.colors.primary}
                        />
                    </View>
                    <Text style={styles.unavailableTitle}>
                        Ce stage n'est pas disponible
                    </Text>
                    <Text style={styles.unavailableText}>
                        {templateName
                            ? `Les inscriptions pour ${templateName} sont fermées pour le moment.`
                            : "Les inscriptions pour ce stage sont fermées pour le moment."}{" "}
                        Contactez-nous pour connaître les prochaines dates.
                    </Text>
                    <SummerHelpBanner
                        track={track}
                        step={1}
                        trackingEvent="stage_funnel_help_clicked"
                        style={styles.unavailableHelp}
                    />
                </View>
            </View>
        )
    }

    const canContinue = !!studentClass

    // ===== Échec init/start NON « indisponible » (réseau, 4xx/5xx, HubSpot
    // introuvable…) : on le DIT (bandeau + réessayer) au lieu de l'avaler — un
    // parcours sans état serveur meurt plus loin (matières, programme, paiement).
    const transientError = !unavailableError && !!(initError || startError)
    const retryStartOrInit = () => {
        if (state.submissionGUID) {
            props.apiRequest(
                stageFunnelService.initStageFromHubSpot,
                {},
                {
                    submission_guid: state.submissionGUID,
                    leadIntent: state.leadIntent,
                    templateSlug: state.templateSlug,
                }
            )
            return
        }
        maybeStartFunnel({
            firstname: state.studentFirstname || route.params?.firstname,
            klass: studentClass || state.studentClass,
            email: state.parentEmail || route.params?.email,
            rentree: deriveClassRentree(studentClass || state.studentClass),
        })
    }
    // Le parcours d'un lien qui nomme la famille et sa classe se crée au
    // montage : tant que le serveur n'a pas dit s'il la connaît, ni la question
    // de la classe ni « Continuer » ne s'affichent (ils disparaîtraient
    // aussitôt pour un abonné). Un échec, lui, s'affiche : le bandeau
    // « Réessayer » vit avec la question.
    const startsOnMount =
        !awaitingStart &&
        !state.funnelStateId &&
        !effectiveReduxStateId &&
        !state.submissionGUID &&
        !route.params?.submissionGUID &&
        !route.params?.submission_guid &&
        !!knownEmail &&
        !!knownFirstname &&
        !!(state.studentClass || normaliseClass(route.params?.class))
    // La réponse du serveur précède d'un rendu la redirection de l'abonné.
    const openingFromLink =
        !transientError &&
        (state.profileKnown ||
            (reduxProfileKnown && !!effectiveReduxStateId) ||
            startsOnMount ||
            (startInFlight && !awaitingStart))

    const errorBanner = transientError ? (
        <View style={styles.errorBanner}>
            <TextM wrap style={styles.errorBannerText}>
                Impossible d'initialiser votre parcours. Vérifiez votre
                connexion puis réessayez.
            </TextM>
            <TextM
                style={styles.errorBannerRetry}
                onPress={retryStartOrInit}
                accessibilityRole="button"
            >
                Réessayer
            </TextM>
        </View>
    ) : null

    // Titre seul (réutilisé étroit/large).
    const titleBlock = (
        <TextM wrap style={styles.title}>
            En quelle classe est {firstname} ?
        </TextM>
    )

    // UNE seule grille : la classe ACTUELLE de l'élève.
    //
    // La double saisie (« cette année » + « à la rentrée ») servait la période
    // de bascule, où les deux pouvaient différer selon la date d'achat. Cette
    // période est passée : demander deux fois la même chose au premier écran
    // d'un funnel d'acquisition coûte des conversions pour rien.
    const chipsBlock = (
        <>
            {errorBanner}
            <ClassChipGridM
                options={classOptions}
                selectedIds={studentClass ? [studentClass] : []}
                onToggle={onSelectClass}
            />

            {/* Entrée URL nue : champs d'identité manquants collectés INLINE
                (uniquement ceux qui manquent) — sans eux, aucun état serveur
                n'est créé et le funnel meurt plus loin. */}
            {needsLeadCapture && (
                <>
                    <TextM wrap style={styles.sectionLabelSpaced}>
                        Vos coordonnées
                    </TextM>
                    <TextM wrap style={styles.leadCaptureHint}>
                        Pour enregistrer le parcours et vous envoyer la
                        confirmation.
                    </TextM>
                    {!knownFirstname && (
                        <TextInputM
                            placeholder="Prénom de votre enfant"
                            name="firstname"
                            control={leadControl}
                            errors={leadErrors}
                            contentContainerStyle={styles.leadCaptureInput}
                        />
                    )}
                    {!knownEmail && (
                        <TextInputM
                            placeholder="Votre adresse email"
                            name="email"
                            control={leadControl}
                            errors={leadErrors}
                            keyboardType="email-address"
                            textContentType="emailAddress"
                            autoCapitalize="none"
                            autoCorrect={false}
                            contentContainerStyle={styles.leadCaptureInput}
                        />
                    )}
                </>
            )}
        </>
    )

    const ctaFooter = (
        <View style={styles.footer}>
            <FilledButtonM
                label="Continuer"
                onPress={onContinuePress}
                loading={awaitingStart}
                disabled={!canContinue}
                color={
                    canContinue ? theme.colors.primary : theme.colors.lightMore
                }
                style={{ width: "100%", marginBottom: 0 }}
            />
        </View>
    )

    return (
        <FunnelStepLayout
            scene={FUNNEL_SCENES.classe}
            contentContainerStyle={styles.content}
            header={
                <FunnelStepHeader
                    step={1}
                    total={FUNNEL_TOTAL_STEPS}
                    hideBack
                />
            }
            footer={openingFromLink ? null : ctaFooter}
        >
            {openingFromLink ? (
                <ActivityIndicator
                    color={theme.colors.primary}
                    style={styles.opening}
                />
            ) : (
                <>
                    {titleBlock}
                    {chipsBlock}
                </>
            )}
        </FunnelStepLayout>
    )
}

const styles = StyleSheet.create({
    root: {
        flex: 1,
        alignItems: "center",
        backgroundColor: theme.colors.whiteAlmost,
    },
    frame: { flex: 1, backgroundColor: theme.colors.white },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 6,
    },
    // TextM ne supporte qu'un style OBJET : pas de composition possible.
    sectionLabelSpaced: {
        fontFamily: "Montserrat-Bold",
        fontSize: 15,
        color: theme.colors.primary,
        marginTop: 24,
        marginBottom: 10,
    },
    // --- collecte d'identité inline (entrée URL nue) ---
    leadCaptureHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        lineHeight: 17,
        color: theme.colors.grey,
        marginTop: -6,
        marginBottom: 4,
    },
    leadCaptureInput: { width: "100%" },
    footer: {
        paddingHorizontal: 20,
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },

    // --- bandeau d'erreur init/start (transitoire, avec retry) ---
    errorBanner: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        backgroundColor: theme.colors.errorLight || "#FDECEC",
        borderRadius: 10,
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginBottom: 14,
    },
    errorBannerText: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        lineHeight: 17,
        color: theme.colors.error,
    },
    errorBannerRetry: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.error,
        textDecorationLine: "underline",
    },

    // --- écran « stage indisponible » ---
    unavailableFrame: {
        alignItems: "center",
        paddingHorizontal: 24,
        paddingTop: 48,
    },
    unavailableLogo: { width: 120, height: 36, marginBottom: 28 },
    unavailableIcon: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: theme.colors.primaryLighter,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16,
    },
    unavailableTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 20,
        color: theme.colors.primary,
        textAlign: "center",
        marginBottom: 8,
    },
    unavailableText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 14,
        lineHeight: 20,
        color: theme.colors.grey,
        textAlign: "center",
    },
    unavailableHelp: { alignSelf: "stretch" },

    // --- narrow (mobile) : bandeau pinné + parallaxe ---
    content: { paddingBottom: 28 },
    opening: { marginTop: 40 },

    // --- wide (desktop split) ---
})

const mapStateToProps = (state) => ({
    reduxFunnelStateId: state.data?.stageFunnel?.funnelStateId ?? null,
    reduxInitUser: state.data?.stageFunnel?.initUser ?? null,
    reduxTemplateSlug: state.data?.stageFunnel?.templateSlug ?? null,
    reduxProfileKnown: !!state.data?.stageFunnel?.profileKnown,
    reduxStudentClass: state.data?.stageFunnel?.studentClass ?? null,
    initError: state.api?.initStageFromHubSpot?.error ?? null,
    startError: state.api?.startStageFunnel?.error ?? null,
    startInFlight: !!state.api?.startStageFunnel?.request,
})

const mapDispatchToProps = { apiRequest, apiIdle }

export default connect(mapStateToProps, mapDispatchToProps)(StageClassScreen)
